// Pannel.tsx

import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Divider,
  Tooltip,
  useTheme,
  useMediaQuery,
  Slide,
} from "@mui/material";

import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import LogoutIcon from "@mui/icons-material/Logout";

import DashboardIcon from "@mui/icons-material/Dashboard";
import PeopleIcon from "@mui/icons-material/People";
import WorkIcon from "@mui/icons-material/Work";
import PaymentIcon from "@mui/icons-material/Payment";
import SchoolIcon from "@mui/icons-material/School";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import SettingsIcon from "@mui/icons-material/Settings";
import GroupIcon from "@mui/icons-material/Group";
import EventIcon from "@mui/icons-material/Event";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import QuizIcon from "@mui/icons-material/Quiz";
import UpdateIcon from "@mui/icons-material/Update";

type NavItem = {
  label: string;
  path: string;
  icon?: React.ReactNode;
  adminOnly?: boolean;
  requiresPackage?: boolean;
  requiresAffiliate?: boolean;
};

const Pannel: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  const isXs = useMediaQuery(
    theme.breakpoints.down("sm")
  );

  const isMdUp = useMediaQuery(
    theme.breakpoints.up("md")
  );

  const [drawerOpen, setDrawerOpen] =
    useState(false);

  const [packageId, setPackageId] =
    useState<number>(0);

  const [isAdmin, setIsAdmin] =
    useState<boolean>(false);

  const [growniverseId, setGrowniverseId] =
    useState<string>("");

  const [nameOfUser, setNameOfUser] =
    useState<string>("");

  const [profileImage, setProfileImage] =
    useState<string | null>(null);

  const [affiliateApproved, setAffiliateApproved] =
    useState<boolean>(false);

  const hasRun = useRef(false);

  // ============================================================
  // LOAD USER DATA
  // ============================================================

  useEffect(() => {
    if (hasRun.current) return;

    const appKey =
      localStorage.getItem("appkey");

    if (!appKey) {
      window.location.href = "/";
      return;
    }

    try {
      const storedProfile =
        localStorage.getItem(
          "userProfile"
        );

      if (storedProfile) {
        const userProfile =
          JSON.parse(storedProfile);

        setIsAdmin(
          Boolean(userProfile.isAdmin)
        );

        setPackageId(
          Number(
            userProfile.packageId ?? 0
          )
        );

        setGrowniverseId(
          String(
            userProfile.growniverseId ?? ""
          )
        );

        setNameOfUser(
          userProfile.fullName ?? ""
        );

        setProfileImage(
          userProfile.profileImage ?? null
        );

        setAffiliateApproved(
          Boolean(
            userProfile.affiliateApproved
          )
        );
      }
    } catch (err) {
      console.warn(
        "Invalid userProfile in localStorage",
        err
      );
    }

    hasRun.current = true;
  }, []);

  // ============================================================
  // LOGOUT
  // ============================================================

  const logoutUser = () => {
    localStorage.clear();
    window.location.href = "/";
  };

  // ============================================================
  // NAVIGATION
  // ============================================================

  const handleNavigate = (
    path: string
  ) => {
    navigate(path);

    if (isXs) {
      setDrawerOpen(false);
    }
  };

  // ============================================================
  // NAVIGATION LIST
  // ============================================================

  const navList: NavItem[] = [
    {
      label: "Dashboard",
      path: "/dashboard",
      icon: <DashboardIcon />,
      requiresPackage: true,
      requiresAffiliate: true,
    },

    {
      label: "Payouts",
      path: "/payouts",
      icon: <PaymentIcon />,
      requiresPackage: true,
      requiresAffiliate: true,
    },

    {
      label: "My Kyc",
      path: "/kyc",
      icon: <PeopleIcon />,
      requiresPackage: true,
      requiresAffiliate: true,
    },

    {
      label: "Training",
      path: "/training",
      icon: <SchoolIcon />,
      requiresPackage: true,
      requiresAffiliate: true,
    },

    {
      label: "Wallet",
      path: "/mywallet",
      icon: <PaymentIcon />,
      requiresPackage: true,
      requiresAffiliate: true,
    },

    {
      label: "Certification",
      path: "/certification",
      icon: <SchoolIcon />,
      requiresPackage: true,
      requiresAffiliate: true,
    },

    {
      label: "My Courses",
      path: "/mycourses",
      icon: <WorkIcon />,
      requiresPackage: true,
    },

    {
      label: "Profile",
      path: "/profile",
      icon: <PeopleIcon />,
      requiresPackage: true,
    },

    {
      label: "Support",
      path: "/support",
      icon: <SupportAgentIcon />,
      requiresPackage: true,
    },

    // ADMIN
    {
      label: "Package",
      path: "/packagepayment",
      icon: <PaymentIcon />,
      adminOnly: true,
    },

    {
      label: "Internship",
      path: "/internship",
      icon: <WorkIcon />,
      adminOnly: true,
    },

    {
      label: "Turnover",
      path: "/admin",
      icon: <DashboardIcon />,
      adminOnly: true,
    },

    {
      label: "Kyc",
      path: "/kycdetail",
      icon: <PeopleIcon />,
      adminOnly: true,
    },

    {
      label: "WithDraw",
      path: "/withdrawrequests",
      icon: <PaymentIcon />,
      adminOnly: true,
    },

    {
      label: "Users",
      path: "/userdetail",
      icon: <PeopleIcon />,
      adminOnly: true,
    },

    {
      label: "Update User",
      path: "/updateuser",
      icon: <UpdateIcon />,
      adminOnly: true,
    },

    {
      label: "Courses",
      path: "/admincourses",
      icon: <SchoolIcon />,
      adminOnly: true,
    },

    {
      label: "Wallet (Admin)",
      path: "/adminwallet",
      icon: <PaymentIcon />,
      adminOnly: true,
    },

    {
      label: "Team",
      path: "/qualityUser",
      icon: <GroupIcon />,
      adminOnly: true,
    },

    {
      label: "MCQ",
      path: "/courseQuestion",
      icon: <QuizIcon />,
      adminOnly: true,
    },

    {
      label: "Coupon",
      path: "/couponCode",
      icon: <LocalOfferIcon />,
      adminOnly: true,
    },

    {
      label: "Event",
      path: "/eventandpayment",
      icon: <EventIcon />,
      adminOnly: true,
    },

    {
      label: "Customized",
      path: "/customized",
      icon: <SettingsIcon />,
      adminOnly: true,
    },

    {
      label: "Notification",
      path: "/notification",
      icon: <SettingsIcon />,
      adminOnly: true,
    },
  ];

  // ============================================================
  // PROFILE IMAGE
  // ============================================================

  const renderProfileImage = (
    size: number
  ) => {
    if (profileImage) {
      return (
        <img
          src={`data:image/jpeg;base64,${profileImage}`}
          alt="Profile"
          style={{
            width: size,
            height: size,
            borderRadius: "50%",
            objectFit: "cover",
            display: "block",
          }}
        />
      );
    }

    return (
      <Avatar
        sx={{
          width: size,
          height: size,
          background:
            "linear-gradient(135deg, #00f6ff, #8b5cff)",
          color: "#031014",
          fontWeight: 900,
          fontSize:
            size * 0.42,
        }}
      >
        {nameOfUser
          ?.trim()
          ?.charAt(0)
          ?.toUpperCase() || "U"}
      </Avatar>
    );
  };

  // ============================================================
  // NAV ITEM
  // ============================================================

  const renderListItem = (
    item: NavItem
  ) => {
    if (
      item.adminOnly &&
      !isAdmin
    ) {
      return null;
    }

    if (
      item.requiresPackage &&
      packageId <= 0
    ) {
      return null;
    }

    if (
      item.requiresAffiliate &&
      !affiliateApproved
    ) {
      return null;
    }

    const active =
      location.pathname ===
      item.path;

    return (
      <ListItemButton
        key={item.path}
        onClick={() =>
          handleNavigate(
            item.path
          )
        }
        sx={{
          position: "relative",
          overflow: "hidden",

          minHeight: 50,

          mb: 0.8,
          px: 1.5,

          borderRadius:
            "10px",

          color: active
            ? "#ffffff"
            : "#aeb9c8",

          border: active
            ? "1px solid rgba(0,246,255,0.20)"
            : "1px solid transparent",

          background: active
            ? "linear-gradient(90deg, rgba(0,246,255,0.12), rgba(139,92,255,0.08))"
            : "transparent",

          transition:
            "all 0.22s ease",

          "&:hover": {
            transform:
              "translateX(5px)",

            background:
              "linear-gradient(90deg, rgba(0,246,255,0.09), rgba(139,92,255,0.06))",

            borderColor:
              "rgba(0,246,255,0.15)",

            color: "#ffffff",
          },

          "&::before": active
            ? {
                content: '""',
                position:
                  "absolute",
                left: 0,
                top: "20%",
                bottom: "20%",
                width: 3,
                borderRadius:
                  "0 5px 5px 0",
                background:
                  "#00f6ff",
                boxShadow:
                  "0 0 12px #00f6ff",
              }
            : {},
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 40,

            color: active
              ? "#00f6ff"
              : "#8f9dad",

            transition:
              "all 0.2s ease",

            "& svg": {
              fontSize: 21,
            },
          }}
        >
          {item.icon}
        </ListItemIcon>

        <ListItemText
          primary={
            item.label
          }
          primaryTypographyProps={{
            fontSize: "13px",
            fontWeight:
              active
                ? 750
                : 550,
            color:
              active
                ? "#ffffff"
                : "#c3ccd8",
          }}
        />

        {active && (
          <span
            style={{
              width: 5,
              height: 5,
              borderRadius:
                "50%",
              background:
                "#00f6ff",
              boxShadow:
                "0 0 10px #00f6ff",
            }}
          />
        )}
      </ListItemButton>
    );
  };

  // ============================================================
  // MAIN UI
  // ============================================================

  return (
    <Slide
      direction="down"
      in
      mountOnEnter
      unmountOnExit
    >
      <Box
        sx={{
          flexGrow: 1,
          position:
            "relative",
          zIndex: 1200,
        }}
      >
        {/* ======================================================
            HEADER
        ====================================================== */}

        <AppBar
          position="static"
          elevation={0}
          sx={{
            position:
              "relative",

            overflow:
              "hidden",

            background:
              "linear-gradient(135deg, rgba(5,8,16,0.98), rgba(8,12,24,0.96))",

            borderBottom:
              "1px solid rgba(0,246,255,0.14)",

            boxShadow:
              "0 8px 35px rgba(0,0,0,0.45)",

            backdropFilter:
              "blur(20px)",
          }}
        >
          {/* Background grid */}

          <Box
            sx={{
              position:
                "absolute",
              inset: 0,
              pointerEvents:
                "none",
              opacity: 0.08,

              backgroundImage:
                "linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)",

              backgroundSize:
                "35px 35px",
            }}
          />

          {/* Cyan glow */}

          <Box
            sx={{
              position:
                "absolute",
              width: 220,
              height: 220,
              left: -100,
              top: -150,
              borderRadius:
                "50%",
              background:
                "rgba(0,246,255,0.08)",
              filter:
                "blur(80px)",
              pointerEvents:
                "none",
            }}
          />

          {/* Purple glow */}

          <Box
            sx={{
              position:
                "absolute",
              width: 240,
              height: 240,
              right: -100,
              top: -160,
              borderRadius:
                "50%",
              background:
                "rgba(139,92,255,0.09)",
              filter:
                "blur(85px)",
              pointerEvents:
                "none",
            }}
          />

          <Toolbar
            sx={{
              position:
                "relative",
              zIndex: 2,

              minHeight: {
                xs: 70,
                sm: 82,
                md: 92,
              },

              px: {
                xs: 1.5,
                sm: 3,
                md: 5,
              },
            }}
          >
            {/* ==================================================
                MENU BUTTON
            ================================================== */}

            <Tooltip
              title="Open navigation"
              arrow
            >
              <IconButton
                onClick={() =>
                  setDrawerOpen(
                    true
                  )
                }
                aria-label="menu"
                sx={{
                  width: {
                    xs: 42,
                    sm: 48,
                  },

                  height: {
                    xs: 42,
                    sm: 48,
                  },

                  mr: {
                    xs: 1.5,
                    sm: 2,
                  },

                  color:
                    "#dce7f3",

                  border:
                    "1px solid rgba(0,246,255,0.20)",

                  borderRadius:
                    "10px",

                  background:
                    "rgba(0,246,255,0.035)",

                  transition:
                    "all 0.2s ease",

                  "&:hover": {
                    color:
                      "#00f6ff",

                    background:
                      "rgba(0,246,255,0.09)",

                    borderColor:
                      "rgba(0,246,255,0.45)",

                    boxShadow:
                      "0 0 18px rgba(0,246,255,0.12)",
                  },
                }}
              >
                <MenuIcon />
              </IconButton>
            </Tooltip>

            {/* ==================================================
                USER INFO
            ================================================== */}

            <Box
              onClick={() =>
                navigate(
                  "/profile"
                )
              }
              sx={{
                display:
                  "flex",
                alignItems:
                  "center",
                gap: {
                  xs: 1,
                  sm: 1.5,
                },

                cursor:
                  "pointer",

                minWidth: 0,
              }}
            >
              {/* Avatar ring */}

              <Box
                sx={{
                  width: {
                    xs: 42,
                    sm: 50,
                  },

                  height: {
                    xs: 42,
                    sm: 50,
                  },

                  padding:
                    "2px",

                  borderRadius:
                    "50%",

                  display:
                    "flex",

                  alignItems:
                    "center",

                  justifyContent:
                    "center",

                  background:
                    "conic-gradient(#00f6ff, #8b5cff, #00f6ff)",

                  boxShadow:
                    "0 0 18px rgba(0,246,255,0.16)",
                }}
              >
                <Box
                  sx={{
                    width:
                      "100%",
                    height:
                      "100%",
                    borderRadius:
                      "50%",
                    overflow:
                      "hidden",
                    display:
                      "flex",
                    alignItems:
                      "center",
                    justifyContent:
                      "center",
                    background:
                      "#080c15",
                    border:
                      "2px solid #080c15",
                  }}
                >
                  {renderProfileImage(
                    isXs
                      ? 34
                      : 42
                  )}
                </Box>
              </Box>

              {/* Name */}

              <Box
                sx={{
                  minWidth: 0,
                  display: {
                    xs: "none",
                    sm: "block",
                  },
                }}
              >
                <Typography
                  sx={{
                    color:
                      "#ffffff",

                    fontSize: {
                      sm: "14px",
                      md: "16px",
                    },

                    fontWeight:
                      750,

                    lineHeight: 1.2,

                    overflow:
                      "hidden",

                    textOverflow:
                      "ellipsis",

                    whiteSpace:
                      "nowrap",

                    maxWidth: {
                      sm: 170,
                      md: 260,
                    },
                  }}
                >
                  {nameOfUser ||
                    "User"}
                </Typography>

                <Box
                  sx={{
                    display:
                      "flex",
                    alignItems:
                      "center",
                    gap: 0.7,
                    mt: 0.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius:
                        "50%",
                      background:
                        "#00f6ff",
                      boxShadow:
                        "0 0 8px #00f6ff",
                    }}
                  />

                  <Typography
                    sx={{
                      color:
                        "#7f8da0",
                      fontSize:
                        "9px",
                      fontWeight:
                        700,
                      letterSpacing:
                        "1.5px",
                    }}
                  >
                    {isAdmin
                      ? "ADMIN"
                      : "MEMBER"}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                flexGrow: 1,
              }}
            />

            {/* ==================================================
                USER ID / STATUS
            ================================================== */}

            {isMdUp && (
              <Box
                sx={{
                  mr: 2,
                  px: 1.8,
                  py: 0.8,

                  border:
                    "1px solid rgba(0,246,255,0.16)",

                  background:
                    "rgba(0,246,255,0.035)",

                  display:
                    "flex",
                  alignItems:
                    "center",
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius:
                      "50%",
                    background:
                      "#00f6ff",
                    boxShadow:
                      "0 0 9px #00f6ff",
                  }}
                />

                <Typography
                  sx={{
                    color:
                      "#a8b5c5",
                    fontSize:
                      "9px",
                    fontWeight:
                      700,
                    letterSpacing:
                      "1.5px",
                  }}
                >
                  {growniverseId ||
                    "SYSTEM ONLINE"}
                </Typography>
              </Box>
            )}

            {/* ==================================================
                PROFILE BUTTON
            ================================================== */}

            
          </Toolbar>

          {/* Bottom neon line */}

          <Box
            sx={{
              position:
                "absolute",
              bottom: 0,
              left: "20%",
              right: "20%",
              height: "1px",

              background:
                "linear-gradient(90deg, transparent, rgba(0,246,255,0.45), transparent)",

              pointerEvents:
                "none",
            }}
          />
        </AppBar>

        {/* ======================================================
            NAVIGATION DRAWER
        ====================================================== */}

        <Drawer
          anchor="left"
          open={drawerOpen}
          onClose={() =>
            setDrawerOpen(
              false
            )
          }
          PaperProps={{
            sx: {
              width: {
                xs: "88vw",
                sm: 350,
              },

              maxWidth: 350,

              color:
                "#ffffff",

              background:
                "linear-gradient(180deg, #050912 0%, #070b15 55%, #05070d 100%)",

              borderRight:
                "1px solid rgba(0,246,255,0.18)",

              boxShadow:
                "15px 0 60px rgba(0,0,0,0.55)",

              overflow:
                "hidden",
            },
          }}
        >
          {/* ====================================================
              DRAWER GRID
          ==================================================== */}

          <Box
            sx={{
              position:
                "absolute",
              inset: 0,
              pointerEvents:
                "none",
              opacity: 0.07,

              backgroundImage:
                "linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)",

              backgroundSize:
                "35px 35px",
            }}
          />

          {/* ====================================================
              DRAWER CYAN GLOW
          ==================================================== */}

          <Box
            sx={{
              position:
                "absolute",
              width: 260,
              height: 260,
              top: -160,
              left: -100,
              borderRadius:
                "50%",
              background:
                "rgba(0,246,255,0.08)",
              filter:
                "blur(90px)",
              pointerEvents:
                "none",
            }}
          />

          {/* ====================================================
              DRAWER HEADER
          ==================================================== */}

          <Box
            sx={{
              position:
                "relative",
              zIndex: 2,

              p: {
                xs: 2,
                sm: 2.5,
              },

              display:
                "flex",

              alignItems:
                "center",

              justifyContent:
                "space-between",
            }}
          >
            <Box
              sx={{
                display:
                  "flex",

                alignItems:
                  "center",

                gap: 1.5,
              }}
            >
              {/* Profile image */}

              <Box
                sx={{
                  width: 50,
                  height: 50,
                  padding: "2px",
                  borderRadius:
                    "50%",

                  background:
                    "conic-gradient(#00f6ff, #8b5cff, #00f6ff)",

                  boxShadow:
                    "0 0 20px rgba(0,246,255,0.16)",
                }}
              >
                <Box
                  sx={{
                    width:
                      "100%",
                    height:
                      "100%",
                    borderRadius:
                      "50%",
                    overflow:
                      "hidden",
                    background:
                      "#080c15",
                    display:
                      "flex",
                    alignItems:
                      "center",
                    justifyContent:
                      "center",
                    border:
                      "2px solid #080c15",
                  }}
                >
                  {renderProfileImage(
                    42
                  )}
                </Box>
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontSize:
                      "15px",
                    fontWeight:
                      800,
                    color:
                      "#ffffff",
                  }}
                >
                  Growniverse
                </Typography>

                <Typography
                  sx={{
                    mt: 0.4,
                    fontSize:
                      "9px",
                    color:
                      "#8d9aaa",
                    fontWeight:
                      700,
                    letterSpacing:
                      "1.5px",
                  }}
                >
                  {growniverseId ||
                    "ADMIN"}
                </Typography>
              </Box>
            </Box>

            {/* Close */}

            <IconButton
              onClick={() =>
                setDrawerOpen(
                  false
                )
              }
              sx={{
                width: 40,
                height: 40,

                color:
                  "#aebccc",

                border:
                  "1px solid rgba(255,255,255,0.08)",

                borderRadius:
                  "9px",

                "&:hover": {
                  color:
                    "#00f6ff",

                  borderColor:
                    "rgba(0,246,255,0.35)",

                  background:
                    "rgba(0,246,255,0.07)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* ====================================================
              USER STATUS
          ==================================================== */}

          <Box
            sx={{
              position:
                "relative",
              zIndex: 2,

              mx: 2,
              mb: 2,

              p: 1.5,

              border:
                "1px solid rgba(0,246,255,0.14)",

              background:
                "linear-gradient(90deg, rgba(0,246,255,0.055), rgba(139,92,255,0.035))",
            }}
          >
            <Box
              sx={{
                display:
                  "flex",
                alignItems:
                  "center",
                justifyContent:
                  "space-between",
              }}
            >
              <Box>
                <Typography
                  sx={{
                    color:
                      "#ffffff",
                    fontSize:
                      "12px",
                    fontWeight:
                      700,
                  }}
                >
                  {nameOfUser ||
                    "User"}
                </Typography>

                <Typography
                  sx={{
                    mt: 0.4,
                    color:
                      "#7f8da0",
                    fontSize:
                      "9px",
                  }}
                >
                  Account control panel
                </Typography>
              </Box>

              <Box
                sx={{
                  display:
                    "flex",
                  alignItems:
                    "center",
                  gap: 0.6,
                  color:
                    "#00f6ff",
                  fontSize:
                    "8px",
                  fontWeight:
                    800,
                  letterSpacing:
                    "1px",
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius:
                      "50%",
                    background:
                      "#00f6ff",
                    boxShadow:
                      "0 0 9px #00f6ff",
                  }}
                />
                ONLINE
              </Box>
            </Box>
          </Box>

          <Divider
            sx={{
              position:
                "relative",
              zIndex: 2,

              borderColor:
                "rgba(255,255,255,0.07)",
            }}
          />

          {/* ====================================================
              NAVIGATION TITLE
          ==================================================== */}

          <Box
            sx={{
              position:
                "relative",
              zIndex: 2,

              px: 2,
              pt: 2.5,
              pb: 1,
            }}
          >
            <Typography
              sx={{
                color:
                  "#00f6ff",
                fontSize:
                  "9px",
                fontWeight:
                  800,
                letterSpacing:
                  "3px",
              }}
            >
              NAVIGATION
            </Typography>
          </Box>

          {/* ====================================================
              NAVIGATION
          ==================================================== */}

          <Box
            sx={{
              position:
                "relative",
              zIndex: 2,

              px: 1.5,

              overflowY:
                "auto",

              flex: 1,

              "&::-webkit-scrollbar": {
                width: "4px",
              },

              "&::-webkit-scrollbar-thumb": {
                background:
                  "rgba(0,246,255,0.25)",
                borderRadius:
                  "10px",
              },
            }}
          >
            <List disablePadding>
              {navList.map(
                (item) =>
                  renderListItem(
                    item
                  )
              )}
            </List>

            {/* ==================================================
                LOGOUT
            ================================================== */}

            <Divider
              sx={{
                my: 2,
                borderColor:
                  "rgba(255,255,255,0.07)",
              }}
            />

            <List disablePadding>
              <ListItemButton
                onClick={
                  logoutUser
                }
                sx={{
                  minHeight: 50,

                  borderRadius:
                    "10px",

                  border:
                    "1px solid rgba(255,100,120,0.10)",

                  background:
                    "rgba(255,80,100,0.025)",

                  color:
                    "#ff9ca8",

                  transition:
                    "all 0.2s ease",

                  "&:hover": {
                    background:
                      "rgba(255,80,100,0.08)",

                    borderColor:
                      "rgba(255,100,120,0.25)",

                    transform:
                      "translateX(5px)",
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 40,
                    color:
                      "#ff7182",
                  }}
                >
                  <LogoutIcon />
                </ListItemIcon>

                <ListItemText
                  primary="Logout"
                  primaryTypographyProps={{
                    fontSize:
                      "13px",
                    fontWeight:
                      700,
                    color:
                      "#ff9ca8",
                  }}
                />
              </ListItemButton>
            </List>
          </Box>

          {/* ====================================================
              DRAWER FOOTER
          ==================================================== */}

          <Box
            sx={{
              position:
                "relative",
              zIndex: 2,

              p: 2,

              borderTop:
                "1px solid rgba(255,255,255,0.07)",

              background:
                "rgba(0,0,0,0.15)",
            }}
          >
            <Box
              sx={{
                display:
                  "flex",
                justifyContent:
                  "space-between",
                alignItems:
                  "center",
              }}
            >
              <Typography
                sx={{
                  color:
                    "#596678",
                  fontSize:
                    "8px",
                  fontWeight:
                    700,
                  letterSpacing:
                    "1.5px",
                }}
              >
                GROWNIVERSE SYSTEM
              </Typography>

              <Typography
                sx={{
                  color:
                    "#596678",
                  fontSize:
                    "8px",
                  fontWeight:
                    700,
                }}
              >
                v1.0
              </Typography>
            </Box>

            <Box
              sx={{
                mt: 1,
                display:
                  "flex",
                alignItems:
                  "center",
                gap: 0.7,
              }}
            >
              <Box
                sx={{
                  width: 5,
                  height: 5,
                  borderRadius:
                    "50%",
                  background:
                    "#00f6ff",
                  boxShadow:
                    "0 0 8px #00f6ff",
                }}
              />

              <Typography
                sx={{
                  color:
                    "#657386",
                  fontSize:
                    "8px",
                }}
              >
                SECURE CONNECTION
              </Typography>
            </Box>
          </Box>
        </Drawer>
      </Box>
    </Slide>
  );
};

export default Pannel;